import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  SyntheticEvent,
  useRef
} from 'react'
import { useAppConfig } from '../../../../hooks/useAppConfig'
import styled from 'styled-components'
import { color, compose, textStyle } from 'styled-system'
import * as yup from 'yup'
import { GridItem } from '@zuto/core/src/grid'
import { Button } from '@zuto/atoms'
import { useCallbackOnce } from '@zuto/utilities'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { muiTypography } from '@zuto/theme/src/mui/DefaultTheme'
import MuiErrorMessage from '@zuto/atoms/src/Mui/ErrorMessage/MuiErrorMessage'
import debounce from 'lodash.debounce'
import $console from '@zuto/utilities/src/$console'
import ZutoAnalyticsHelper from '@zuto-internal/zuto-analytics-helper'
import postcodeService from '../../../../services/postcode-service'
import AppFormQuestion from '../../../AppFormQuestion'
import QuestionKey from '../../../../Questions/question-key'
import ErrorModal from '../../../ErrorModal'
import AddressManualInput from '../AddressManualInput'
import { AddressDetails, AddressEntryErrors, DisplayState } from '../AddressHistoryContext'
import { validateEntry } from '../AddressHistoryValidation'
import AddressHistoryEntryContext from '../AddressEntry/AddressHistoryEntryContext'
import NavigationButtons from '../../../NavigationButtons/NavigationButtons'
import { questionFontStyle, fireReactGAEvent } from '../../utils'
import { sortAddressList, ResultOption } from '../../../../utils/utils'
import { AddressDetailsProps } from './types'
import { AttributionContext } from '../../../../context'
import { AmplitudeEvents, trackAmplitudeEvent } from '../../../../utils/amplitude-utils'
import { toSnakeCase } from '../../../../utils/string-utils'
import withFade from '../../../HOC/withFade'
import { useAppFormEventsService } from '../../../../hooks/useAppFormEventsService'

const DisplayStateButton = styled(Button)`
  ${(props) => compose(color)({ ...props, color: 'material.1' })}
  ${(props) => compose(textStyle)({ ...props, textStyle: 'body.current.regular' })} 
  /* Added as a work around for iPhone sticky hover (Native bug) 
  to prevent colour changing when clicking continue/previous buttons*/ 
  @media only screen and (max-width: 767px) {
    &:hover {
      color: ${(props) => props.theme.colors.material[1]};
      svg {
        path {
          fill: ${(props) => props.theme.colors.material[1]};
        }
      }
    }
    svg {
      &:hover {
        path {
          fill: ${(props) => props.theme.colors.material[1]};
        }
      }
    }
  }
  min-width: auto;
  svg {
    order: 1;
    path {
      fill: ${(props) => props.theme.colors.material[1]};
    }
  }
  &.manual-entry-toggle,
  &.postcode-search-toggle {
    ${muiTypography.body.label.small}
  }
  &.manual-entry-toggle {
    margin-top: 160px;
  }
`

const AddressDetailsQuestionV2 = ({
  existingDetails = {},
  entryId,
  disablePrevious = false,
  existingDisplayState = 'SEARCH_RESULTS',
  existingLoqateResults,
  existingPostcodeSearchLabel,
  existingPostcodeSearchAttempted = false
}: AddressDetailsProps) => {
  const appConfig = useAppConfig()
  const attribution = useContext(AttributionContext)
  const { dispatch } = useContext(AddressHistoryEntryContext)
  const eventsService = useAppFormEventsService()
  const [searchError, setSearchError] = useState('')
  const [addressList, setAddressList] = useState<ResultOption[] | undefined>(existingLoqateResults)
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(false)
  const [addressDetails, setAddressDetails] = useState<AddressDetails>(existingDetails)
  const [errors, setErrors] = useState<AddressEntryErrors>({})
  const [nextButtonCb, resetNextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()
  const [manualAddressAnswers, setManualAddressAnswers] = useState<AddressDetails>(existingDetails)
  // The list starts open unless an address was already chosen, and the postcode input is read-only while one is chosen
  const [autocompleteOpen, setAutocompleteOpen] = useState(!existingPostcodeSearchLabel || existingPostcodeSearchAttempted)
  const [postcodeEnteredAddressNotSelected, setPostcodeEnteredAddressNotSelected] = useState<boolean>(existingPostcodeSearchAttempted)
  const [postcodeSearchSelectedLabel, setPostcodeSearchSelectedLabel] = useState<string | null | undefined>(existingPostcodeSearchLabel)
  const [postcodeSearchReadOnly, setPostcodeSearchReadOnly] = useState<boolean>(!!existingPostcodeSearchLabel && !existingPostcodeSearchAttempted)
  const [postcodeSearch, setPostcodeSearch] = useState(existingDetails.postcode ?? (postcodeEnteredAddressNotSelected && postcodeSearchSelectedLabel ? postcodeSearchSelectedLabel : '') ?? '')
  const [noLocationsFoundText, setNoLocationsFoundText] = useState<string>('')
  const [displayState, setDisplayState] = useState<DisplayState>(existingDisplayState)
  const [shouldBlur, setShouldBlur] = useState(false)
  const canContinue = Boolean(addressDetails.postcode || addressDetails.id || (displayState === 'MANUAL_ENTRY' && postcodeSearchSelectedLabel))
  const postcodeSearchRef = useRef<HTMLInputElement>(null)
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()

  useEffect(() => {
    /** Workaround for IOS Bug where focus does not work on continue/previous after clearing input.
     * Keyboard therefore doesn't show up and user can't type updated value. If we unfocus the input, forces user to click it again to bring keyboard up.
     **/
    if (shouldBlur && postcodeSearchRef.current) {
      postcodeSearchRef.current?.blur()
    }
  }, [shouldBlur])

  useEffect(() => {
    const label = `qst.addressHistory.${parseInt(entryId, 10) - 1}.postcodeAnywhere`
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label,
      nonInteraction: true
    })
    eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: label })
    trackAmplitudeEvent(AmplitudeEvents.ApplicationQuestionViewed, { question_label: toSnakeCase(label) })
  }, [])

  const saveAnswers = (answers: AddressDetails) => {
    dispatch({
      type: 'UPDATE_ADDRESS_DETAILS',
      value: answers
    })
  }

  const savePostcodeLabel = (label: string | null) => {
    dispatch({
      type: 'UPDATE_POSTCODE_SEARCH_LABEL',
      value: label
    })
  }

  const saveLoqateResults = (results: ResultOption[] | undefined) => {
    dispatch({
      type: 'UPDATE_LOQATE_RESULTS',
      value: results
    })
  }

  const saveDisplayState = (displayStateValue: DisplayState) => {
    if (displayStateValue === 'SEARCH_RESULTS') {
      dispatch({
        type: 'UPDATE_DISPLAY_STATE',
        value: 'SEARCH_RESULTS'
      })
      setDisplayState('SEARCH_RESULTS')
    } else {
      dispatch({
        type: 'UPDATE_DISPLAY_STATE',
        value: 'MANUAL_ENTRY'
      })
      setDisplayState('MANUAL_ENTRY')
    }
    // a postcode typed without an address chosen keeps the list open whenever the search view shows
    if (postcodeSearchSelectedLabel && postcodeEnteredAddressNotSelected) {
      setAutocompleteOpen(true)
    }
  }

  const saveHasPostcodeSearchBeenAttempted = (inputValue: boolean) => {
    dispatch({
      type: 'UPDATE_POSTCODE_SEARCH_ATTEMPTED',
      value: inputValue
    })
  }

  const isValidUKPostcode = (postcode: string): boolean =>
    yup
      .string()
      .required('Please enter a postcode')
      .matches(/^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/)
      .max(20)
      .label('Postcode')
      .isValidSync(postcode)

  const getSelectedAddress = async (id: string): Promise<AddressDetails> => postcodeService(appConfig).retrieveAddress(id, 'AddressRedesignV2')

  const postcodeSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const postcode = e.target.value ? e.target.value.replace(/\s/g, '').toLowerCase() : ''
    if (isValidUKPostcode(postcode)) {
      setSearchError('')
    }
    setPostcodeSearch(postcode)

    if (!isValidUKPostcode(postcode)) {
      setSearchError('Please enter a valid UK postcode')
      setAddressList([])
      return
    }
    setSearchError('')
    setLoading(true)

    postcodeService(appConfig)
      .getAddresses(postcode)
      .then((results) => {
        if (results.length === 0) {
          setNoLocationsFoundText('No Locations Found')
          try {
            fireReactGAEvent({
              category: 'Application Form',
              action: 'Postcode Lookup',
              label: 'No locations found'
            })
            zutoAnalyticsHelper.sendCustomEvent({
              eventType: 'No locations found',
              eventCategory: 'Loqate',
              experiment: 'AddressRedesignV2'
            })
          } catch (error) {
            $console.error('zutoAnalyticsHelper.sendCustomEvent error', error)
          }
          return
        }

        const sortedAddressList = sortAddressList(results as ResultOption[])

        setAddressList([...sortedAddressList])

        try {
          zutoAnalyticsHelper.sendCustomEvent({
            eventType: 'loqateFind - addressDetailsV2',
            eventCategory: 'Loqate',
            experiment: 'AddressRedesignV2',
            eventMessage: 'Success'
          })
        } catch (error) {
          $console.error('zutoAnalyticsHelper.sendCustomEvent error', error)
        }
      })
      .catch((error) => {
        setApiError(true)
        try {
          zutoAnalyticsHelper.sendCustomEvent({
            eventType: 'loqateFind Failed - addressDetailsV2',
            eventCategory: 'Loqate',
            experiment: 'AddressRedesignV2',
            eventMessage: error ? error : 'loqateFind failed'
          })
        } catch (error) {
          $console.error('zutoAnalyticsHelper.sendCustomEvent error', error)
        }
      })
      .finally(() => setLoading(false))
  }

  const debouncedChangeHandler = useMemo(() => debounce(postcodeSearchChange, 1000), [])

  useEffect(
    () => () => {
      debouncedChangeHandler.cancel()
    },
    []
  )

  const showManualEntry = () => {
    if (displayState !== 'MANUAL_ENTRY') {
      const manualLabel = `qst.addressHistory.${parseInt(entryId, 10) - 1}.manualAddress`
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Question Visible',
        label: manualLabel,
        nonInteraction: true
      })
      eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: manualLabel })
      trackAmplitudeEvent(AmplitudeEvents.ApplicationQuestionViewed, { question_label: toSnakeCase(manualLabel) })
      setErrors({})
      saveDisplayState('MANUAL_ENTRY')
    }
  }

  const showPostcodeSearch = () => {
    if (displayState !== 'SEARCH_RESULTS') {
      const postcodeLabel = `qst.addressHistory.${parseInt(entryId, 10) - 1}.postcodeAnywhere`
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Question Visible',
        label: postcodeLabel,
        nonInteraction: true
      })
      eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: postcodeLabel })
      trackAmplitudeEvent(AmplitudeEvents.ApplicationQuestionViewed, { question_label: toSnakeCase(postcodeLabel) })
      setSearchError('')
      setErrors({})
      saveDisplayState('SEARCH_RESULTS')
      if (addressList?.length === 0) {
        setPostcodeSearchSelectedLabel('')
      }
    }
  }

  const closeError = () => {
    setApiError(false)
    saveDisplayState('MANUAL_ENTRY')
  }

  const validate = (): boolean => {
    const result = validateEntry({ addressDetails, entrySource: 'FORM' }, attribution, false, true, false)
    if (typeof result === 'boolean') return result
    const prettyErrors: Record<string, string> = {}
    result.forEach((validationError) => {
      if (!validationError.path) return
      const [errorMessage] = validationError.errors
      prettyErrors[validationError.path.toString()] = errorMessage
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Validation Error',
        label: validationError.path.toString(),
        nonInteraction: true
      })
    })
    setErrors(prettyErrors)
    return false
  }

  const previousHandler = () => {
    if (addressDetails) {
      saveAnswers(addressDetails)
    }
    if (addressList) {
      saveLoqateResults(addressList)
    }
    if (postcodeSearchSelectedLabel) {
      savePostcodeLabel(postcodeSearchSelectedLabel)
    }
    saveHasPostcodeSearchBeenAttempted(postcodeEnteredAddressNotSelected)

    dispatch({
      type: 'PREVIOUS'
    })
  }

  const searchNextHandler = () => {
    if (!addressDetails) {
      resetNextButtonCb()
      return
    }
    if (addressDetails) {
      saveAnswers(addressDetails)
    }
    if (addressList) {
      saveLoqateResults(addressList)
    }
    if (postcodeSearchSelectedLabel) {
      savePostcodeLabel(postcodeSearchSelectedLabel)
    }
    saveHasPostcodeSearchBeenAttempted(postcodeEnteredAddressNotSelected)

    dispatch({
      type: 'NEXT'
    })
  }

  const manualNextHandler = () => {
    if (!validate()) {
      resetNextButtonCb()
      return
    }
    if (addressDetails) {
      saveAnswers(addressDetails)
    }
    if (addressList) {
      saveLoqateResults(addressList)
    }
    if (postcodeSearchSelectedLabel) {
      savePostcodeLabel(postcodeSearchSelectedLabel)
    }

    dispatch({
      type: 'NEXT'
    })
  }

  const nextHandler = () => {
    switch (displayState) {
      case 'MANUAL_ENTRY':
        manualNextHandler()
        break
      case 'SEARCH_RESULTS':
        searchNextHandler()
        break
      default:
    }
  }

  const updateManualAddressDetails = (name: string, value: string) => {
    setAddressDetails({
      ...addressDetails,
      [name]: value
    })
  }

  const selectedAddressHandler = (event: React.SyntheticEvent<Element, Event>) => {
    const labelText = (event.target as HTMLInputElement).innerText
    setPostcodeSearchSelectedLabel(labelText)
    setPostcodeEnteredAddressNotSelected(false)
    if (labelText) {
      setPostcodeSearchReadOnly(true)
    }
    // the chosen address's id goes into addressDetails, and the effect below fetches its details
    const selectedAddress = addressList?.find((item) => item.label === labelText)
    if (selectedAddress) {
      setAddressDetails({
        id: String(selectedAddress.value)
      })
    }
  }

  useEffect(() => {
    if (addressDetails.id) {
      getSelectedAddress(addressDetails.id).then((addressDetailsResult) => {
        setManualAddressAnswers(addressDetailsResult)
        setAddressDetails(addressDetailsResult)
      })
    }
  }, [addressDetails])

  const postcodeSearchInputHandler = (event: SyntheticEvent<Element, Event>, newValue: string | null, reason: string) => {
    if (event) {
      const elementTarget = event.target as HTMLInputElement
      const { value: elementTargetValue } = elementTarget
      setPostcodeEnteredAddressNotSelected(true)
      setPostcodeSearchSelectedLabel(elementTargetValue)
      setShouldBlur(false)
    }
    if (reason === 'clear') {
      setAddressList([])
      setPostcodeSearchSelectedLabel(null)
      setAddressDetails({})
      savePostcodeLabel(null)
      setPostcodeSearch('')
      setManualAddressAnswers({})
      setPostcodeSearchReadOnly(false)
      setAutocompleteOpen(false)
      setShouldBlur(true)
    }
  }

  const autoCompletePopperText = searchError ? 'Please enter a valid UK postcode' : 'e.g. AB1 2BC'

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="186px"
      >
        <AppFormQuestion
          questionKey={`${QuestionKey.ADDRESS_HISTORY}.address`}
          mt={3}
          mb={2}
          hintText={'We need your address history for the last 3 years. This is so our lenders can identify you and link to your credit history.'}
          textStyle={questionFontStyle}
        >
          {`What ${entryId === '1' ? 'is your address?' : 'was your previous address?'}`}
        </AppFormQuestion>
        <GridItem>
          {displayState === 'SEARCH_RESULTS' && (
            <GridItem gridColumn={['auto / span 9', '1 / span 6']}>
              <Autocomplete
                selectOnFocus={false} // When true, This highlights the input text, which makes the Clear button unclickable on iPhone without unhighlighting it first.
                aria-autocomplete="list"
                aria-label="postcode-entry"
                id="autocomplete-postcode-search"
                open={autocompleteOpen}
                onOpen={() => {
                  setAutocompleteOpen(true)
                }}
                onClose={() => {
                  setAutocompleteOpen(false)
                }}
                onChange={(e) => selectedAddressHandler(e)}
                openOnFocus
                onInputChange={(event, newValue, reason) => postcodeSearchInputHandler(event, newValue, reason)}
                isOptionEqualToValue={(option: ResultOption, value: ResultOption) => option.label === value.label}
                getOptionLabel={(option) => option.label || (postcodeSearchSelectedLabel as string)}
                options={addressList !== undefined ? addressList : []}
                loading={loading}
                noOptionsText={noLocationsFoundText && !searchError ? noLocationsFoundText : autoCompletePopperText}
                clearOnBlur={false}
                // Used to disable the built-in filtering, so now the options list re-renders when addressList state changes
                filterOptions={(x) => x}
                fullWidth
                sx={{ marginTop: '12px' }}
                defaultValue={postcodeSearchSelectedLabel ? { value: '', label: postcodeSearchSelectedLabel ?? '' } : null}
                popupIcon={addressList !== undefined && addressList.length > 0 && <ArrowDropDownIcon data-testid="autocomplete-dropdown" />}
                ListboxProps={{ style: { maxHeight: 160 } }}
                data-testid="autocomplete-postcode-search"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={postcodeSearchRef}
                    autoFocus={Boolean(!postcodeSearchSelectedLabel) || postcodeEnteredAddressNotSelected}
                    className="outlined"
                    label="Enter your postcode"
                    variant="outlined"
                    onChange={debouncedChangeHandler}
                    error={Boolean(searchError)}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: !!postcodeSearchReadOnly
                    }}
                    InputProps={{
                      id: 'postcode-search',
                      type: 'text',
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                              data-testid="autocomplete-loading-spinner"
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
              <MuiErrorMessage
                error={searchError}
                isGroupedOutlined
              />
            </GridItem>
          )}
          <GridItem
            gridColumn="1 / span 12"
            style={{
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
            mb={13}
            mt={0}
          >
            {displayState === 'SEARCH_RESULTS' ? (
              <DisplayStateButton
                className="manual-entry-toggle"
                mb="20px"
                p={0}
                variant="tertiary"
                onClick={showManualEntry}
                data-testid="show-manual-entry"
              >
                Can&apos;t Find Your Address?
              </DisplayStateButton>
            ) : (
              <DisplayStateButton
                className="postcode-search-toggle"
                mb="24px"
                p={0}
                variant="tertiary"
                onClick={showPostcodeSearch}
                data-testid="show-postcode-search"
              >
                Or Search For Your Address
              </DisplayStateButton>
            )}
          </GridItem>
          {displayState === 'MANUAL_ENTRY' && (
            <AddressManualInput
              valueChanged={updateManualAddressDetails}
              postcode={postcodeSearch}
              error={errors}
              existingAnswers={manualAddressAnswers}
            />
          )}
        </GridItem>
        {apiError && <ErrorModal onClick={closeError}>We were unable to retrieve your address at this time, but not to worry, you can enter it manually...</ErrorModal>}
        <NavigationButtons
          nextHandler={() => nextButtonCb(nextHandler)}
          previousHandler={() => previousButtonCb(previousHandler)}
          disableNext={!canContinue}
          disablePrevious={disablePrevious ? 'invisible' : undefined}
        />
      </GridItem>
    </>
  )
}

export default withFade(AddressDetailsQuestionV2)
